import { useState, useCallback, useRef, useEffect } from 'react';
import { Turn, SimulationSessionOptions, SimulationState } from '../types';
import { useSimulationTimer } from './useSimulationTimer';
import { useSimulationAudio, LearnerTurnSpeechMeta, PersonaVoiceConfig } from './useSimulationAudio';
import { useSimulationLifecycle } from './useSimulationLifecycle';
import { stopAllSpeech } from '../../../services/voiceService';
import { SIMULATION_CONSTANTS } from '../constants';

export interface TurnEndedModalState {
  isOpen: boolean;
  reason: 'TURN_LIMIT_REACHED' | 'CONVERSATION_CONCLUDED';
  title: string;
  description: string;
  turnsCount: number;
  maxTurns?: number;
}

export function useSimulationSession({
  attemptId: initialAttemptId,
  scenarioId,
  personaName: initialPersonaProp,
  scenarioTitle: initialTitleProp,
  initialTurnText: _initialTurnProp,
  maxDurationSeconds: initialDurationProp = SIMULATION_CONSTANTS.DEFAULT_DURATION_SECONDS,
  language = SIMULATION_CONSTANTS.DEFAULT_LANGUAGE,
  enabled = true,
  autoPlayInitialTurn = true,
  initialAttemptData,
  onEndSimulation,
  onError,
}: SimulationSessionOptions) {
  const [activeTab, setActiveTab] = useState<'persona' | 'transcript'>('persona');
  const [manualText, setManualText] = useState('');
  const [pendingTranscript, setPendingTranscript] = useState<string>('');
  const [silenceCountdownSeconds, setSilenceCountdownSeconds] = useState<number | null>(null);
  const [turnEndedModal, setTurnEndedModal] = useState<TurnEndedModalState | null>(null);
  const [capturedInterruptions, setCapturedInterruptions] = useState(0);

  const isEndingRef = useRef(false);
  const pendingTranscriptRef = useRef<string>('');
  const pauseDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastChunkTimestampRef = useRef<number>(0);
  const turnListeningStartTimeRef = useRef<number>(Date.now());

  // Stable refs so onAiResponse/onInitialTurn closures always see current values
  // (avoids stale-closure bugs when lifecycle is circular-initialized)
  const lifecycleTurnsRef = useRef<Turn[]>([]);
  const lifecycleBriefingRef = useRef<any>(null);

  // 1. Audio and STT / TTS Subsystem
  const audio = useSimulationAudio({
    language,
    personaName: initialPersonaProp,
    onTranscript: async (finalUtterance: string, meta?: LearnerTurnSpeechMeta) => {
      if (!finalUtterance || isEndingRef.current) return;

      const textToSubmit = finalUtterance.trim();
      if (!textToSubmit) return;

      pendingTranscriptRef.current = textToSubmit;
      setPendingTranscript(textToSubmit);

      // Clean conversational turn submission: complete finalized utterance submitted exactly once
      if (!isEndingRef.current) {
        try {
          await submitLearnerTurn(textToSubmit, meta);
        } finally {
          pendingTranscriptRef.current = '';
          setPendingTranscript('');
        }
      } else {
        pendingTranscriptRef.current = '';
        setPendingTranscript('');
      }
    },
    onInterruption: () => {
      setCapturedInterruptions((n) => n + 1);
    },
    onError,
    enabled,
  });

  // Track learner turn start and reset pending speech when AI speaks
  useEffect(() => {
    if (!audio.isAiSpeaking) {
      turnListeningStartTimeRef.current = Date.now();
      lastChunkTimestampRef.current = 0;
    } else {
      if (pauseDebounceTimerRef.current) {
        clearTimeout(pauseDebounceTimerRef.current);
        pauseDebounceTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setSilenceCountdownSeconds(null);
      pendingTranscriptRef.current = '';
      setPendingTranscript('');
    }
  }, [audio.isAiSpeaking]);

  // 2. Lifecycle & Backend Attempt Subsystem
  const lifecycle = useSimulationLifecycle({
    scenarioId,
    attemptId: initialAttemptId,
    language,
    enabled,
    initialAttemptData,
    onInitialTurn: (turn: Turn) => {
      if (isEndingRef.current) return;
      if (autoPlayInitialTurn === false) {
        // Ensure mic permission check runs in background without premature opening speech
        void audio.ensureMicrophone();
        return;
      }
      const effectivePersona = turn.personaName || turn.speakerName || initialPersonaProp;
      if (turn.text || turn.audioUrl) {
        const launch = () =>
          audio.speakText(turn.text, turn.audioUrl, undefined, {
            personaName: effectivePersona,
            ttsVoice: turn.ttsVoice,
            ttsProvider: turn.ttsProvider,
            country: turn.country,
            tone: turn.tone,
            pitch: turn.pitchPreference,
            rate: turn.speakingPace,
            genderPreference: turn.genderPreference,
          });

        // Mic permission must be proven BEFORE the opening line plays, otherwise a learner
        // who barges in on turn 1 has nothing to be captured with.
        if (audio.micReady) {
          launch();
        } else {
          void audio.ensureMicrophone().finally(launch);
        }
      }
    },
    onAiResponse: (turn: Turn) => {
      if (isEndingRef.current) {
        audio.stopAll();
        return;
      }

      // Read briefing & turns from stable refs — lifecycle object is NOT yet
      // available when this closure is created (circular init), so we must use
      // refs that are kept in sync via a useEffect below.
      const briefing = lifecycleBriefingRef.current;
      const maxTurns =
        briefing?.maxTurns ||
        briefing?.conversationConstraints?.maxTurns ||
        briefing?.endingRules?.maxTurns ||
        20;

      const handleCompletion = () => {
        // Read turns count at speech-end time (not at turn-arrival time) so
        // we count correctly even on mobile where state updates are batched.
        const currentTurnsCount = lifecycleTurnsRef.current.length;
        const isTurnLimitReached = currentTurnsCount >= maxTurns;
        const isConversationEnded = Boolean(turn.isTerminal || turn.conversationEnded);

        if (isTurnLimitReached || isConversationEnded) {
          isEndingRef.current = true;
          audio.stopAll();
          setTurnEndedModal({
            isOpen: true,
            reason: isTurnLimitReached ? 'TURN_LIMIT_REACHED' : 'CONVERSATION_CONCLUDED',
            title: isTurnLimitReached ? 'Turn Limit Reached' : 'Conversation Concluded',
            description: isTurnLimitReached
              ? `You have reached the maximum allowed conversation turns (${currentTurnsCount} of ${maxTurns} turns) for this simulation.`
              : `Your dialogue partner has concluded the conversation (${currentTurnsCount} turns recorded).`,
            turnsCount: currentTurnsCount,
            maxTurns,
          });
        }
      };

      const effectivePersona = turn.personaName || turn.speakerName || initialPersonaProp;
      if (turn.text || turn.audioUrl) {
        audio.speakText(turn.text, turn.audioUrl, handleCompletion, {
          personaName: effectivePersona,
          ttsVoice: turn.ttsVoice,
          ttsProvider: turn.ttsProvider,
          country: turn.country,
          tone: turn.tone,
          pitch: turn.pitchPreference,
          rate: turn.speakingPace,
          genderPreference: turn.genderPreference,
        });
      } else {
        handleCompletion();
      }
    },
    onError,
    onComplete: () => {
      isEndingRef.current = true;
      audio.stopAll();
      if (onEndSimulation) {
        onEndSimulation(lifecycleTurnsRef.current, lifecycle.attemptId);
      }
    },
  });

  // Keep stable refs in sync with lifecycle state so closures always see current values
  useEffect(() => {
    lifecycleTurnsRef.current = lifecycle.turns || [];
  }, [lifecycle.turns]);

  useEffect(() => {
    lifecycleBriefingRef.current = lifecycle.briefing;
  }, [lifecycle.briefing]);

  // Synchronize authoritative voice contract & persona voice configuration to audio engine
  useEffect(() => {
    const vc = (lifecycle as any).voiceContract;
    const persona = lifecycle.briefing?.persona as any;
    const config = persona?.voiceConfig || {};
    const newConfig: PersonaVoiceConfig = {
      personaName: persona?.name || initialPersonaProp,
      genderPreference: persona?.gender || persona?.genderPreference || config?.gender,
      ttsVoice: vc?.ttsVoice || vc?.voiceId || config?.ttsVoice || config?.voiceId,
      ttsProvider: vc?.ttsProvider || (lifecycle as any).effectiveTTSProvider || config?.ttsProvider,
      country: vc?.country || persona?.country || config?.country || (lifecycle.briefing as any)?.country,
      tone: config?.tone || persona?.tone,
      pitch: config?.pitch,
      rate: config?.rate || config?.speakingPace,
    };
    audio.updatePersonaVoiceConfig?.(newConfig);
  }, [lifecycle.voiceContract, lifecycle.briefing, lifecycle.effectiveTTSProvider, initialPersonaProp, audio.updatePersonaVoiceConfig]);

  // 3. Countdown Timer Subsystem
  const timerDuration =
    lifecycle.briefing?.timeLimitMinutes
      ? lifecycle.briefing.timeLimitMinutes * 60
      : lifecycle.briefing?.durationMinutes
      ? lifecycle.briefing.durationMinutes * 60
      : initialDurationProp;

  // A resumed attempt must continue from the server clock instead of restarting
  // its countdown. Freeze the server-derived remaining seconds once per attempt:
  // recomputing Date.now() on every render would keep changing initialSeconds,
  // and useSimulationTimer resets whenever initialSeconds changes.
  const briefingClock = lifecycle.briefing as any;
  const serverClockRef = useRef<{ attemptId: string | null; initialSeconds: number | null }>({
    attemptId: null,
    initialSeconds: null,
  });
  const currentAttemptKey = lifecycle.attemptId || null;
  if (serverClockRef.current.attemptId !== currentAttemptKey) {
    serverClockRef.current = { attemptId: currentAttemptKey, initialSeconds: null };
  }
  if (serverClockRef.current.initialSeconds === null) {
    let remainingFromServer: number | null = null;
    const serverRemaining = briefingClock?.timeRemainingSeconds;
    if (serverRemaining !== undefined && serverRemaining !== null && Number.isFinite(Number(serverRemaining))) {
      remainingFromServer = Math.max(1, Math.round(Number(serverRemaining)));
    } else if (briefingClock?.startedAt) {
      const startedAtMs = new Date(briefingClock.startedAt).getTime();
      if (Number.isFinite(startedAtMs)) {
        const elapsed = Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000));
        remainingFromServer = Math.max(1, Math.round(timerDuration - elapsed));
      }
    }
    if (remainingFromServer !== null) {
      serverClockRef.current = { attemptId: currentAttemptKey, initialSeconds: remainingFromServer };
    }
  }
  const initialTimerSeconds = serverClockRef.current.initialSeconds ?? Math.max(1, Math.round(timerDuration));

  const timer = useSimulationTimer({
    initialSeconds: initialTimerSeconds,
    onExpire: () => {
      // The countdown reaching zero is a timed-out attempt, not a learner
      // confirmation — persist the real completion reason for analytics.
      endSimulation('TIMEOUT');
    },
    isPaused: lifecycle.state === 'completing' || lifecycle.state === 'completed' || lifecycle.state === 'error',
  });

  // 4. Submit Turn Orchestration
  const submitLearnerTurn = useCallback(
    async (text: string, meta?: LearnerTurnSpeechMeta) => {
      const trimmed = text.trim();
      if (!trimmed || isEndingRef.current) return;
      audio.stopSpeaking();
      await lifecycle.submitTurn(trimmed, meta);
    },
    [audio, lifecycle]
  );

  // Barge-In: one call stops the persona, logs the interruption and reopens the mic.
  const handleBargeIn = useCallback(() => {
    stopAllSpeech();
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch {}
    }
    return audio.bargeIn('barge_in');
  }, [audio]);

  // Instant Turn Submission (Done Speaking / Send Now button or Enter key)
  const handleInstantSubmit = useCallback(() => {
    if (pauseDebounceTimerRef.current) {
      clearTimeout(pauseDebounceTimerRef.current);
      pauseDebounceTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setSilenceCountdownSeconds(null);

    // Flush active spoken utterance from audio subsystem immediately
    if (audio.flushAndFinalize) {
      audio.flushAndFinalize();
    }

    const manual = manualText.trim();
    if (manual && !isEndingRef.current) {
      setManualText('');
      submitLearnerTurn(manual);
    }
  }, [audio, manualText, submitLearnerTurn]);

  // 5. End Simulation Orchestration
  const endSimulation = useCallback(async (reason: string = 'LEARNER_CONFIRMED') => {
    isEndingRef.current = true;
    if (pauseDebounceTimerRef.current) clearTimeout(pauseDebounceTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setSilenceCountdownSeconds(null);
    audio.stopAll();
    audio.stopSpeaking();
    const completionReason = typeof reason === 'string' && reason.trim() ? reason.trim() : 'LEARNER_CONFIRMED';
    const elapsedSec = Math.max(1, timerDuration - timer.secondsRemaining);
    return await lifecycle.completeAttempt(completionReason, lifecycle.turns, elapsedSec);
  }, [audio, lifecycle, timerDuration, timer.secondsRemaining]);

  // Derived state
  const personaName =
    lifecycle.briefing?.persona?.name ||
    lifecycle.briefing?.persona?.title ||
    initialPersonaProp ||
    SIMULATION_CONSTANTS.DEFAULT_PERSONA_NAME;

  const scenarioTitle =
    lifecycle.briefing?.title ||
    initialTitleProp ||
    SIMULATION_CONSTANTS.DEFAULT_SCENARIO_TITLE;

  const currentSimulationState: SimulationState =
    lifecycle.state === 'error'
      ? 'error'
      : lifecycle.state === 'completing'
      ? 'completing'
      : lifecycle.state === 'completed'
      ? 'completed'
      : lifecycle.state === 'initializing'
      ? 'initializing'
      : lifecycle.state === 'starting'
      ? 'starting'
      : audio.isAiSpeaking
      ? 'ai_speaking'
      : lifecycle.state === 'processing'
      ? 'processing'
      : audio.isListening
      ? 'listening'
      : 'ready';

  return {
    // Scenario / Persona Data
    attemptId: lifecycle.attemptId,
    personaName,
    scenarioTitle,
    briefing: lifecycle.briefing,
    currentStageIndex: lifecycle.currentStageIndex,
    isLoadingScenario: enabled && (lifecycle.state === 'initializing' || lifecycle.state === 'starting'),
    turns: lifecycle.turns,
    error: lifecycle.error,
    isReconnecting: lifecycle.isReconnecting,
    initProgressStage: lifecycle.initProgressStage,
    reconnect: lifecycle.reconnect,

    // Audio / STT / TTS
    isMuted: audio.isMuted,
    toggleMute: audio.toggleMute,
    isAiSpeaking: audio.isAiSpeaking,
    isAudioLoading: audio.isAudioLoading,
    isAudioReady: audio.isAudioReady,
    isAutoplayBlocked: audio.isAutoplayBlocked,
    resumeAutoplay: audio.resumeAutoplay,
    audioLoadingError: audio.audioLoadingError,
    isListening: audio.isListening,
    interimTranscript: audio.interimTranscript,
    pendingTranscript,
    silenceCountdownSeconds: audio.silenceCountdownSeconds ?? silenceCountdownSeconds,
    audioLevel: audio.audioLevel,
    micPermissionDenied: audio.micPermissionDenied,
    micReady: audio.micReady,
    retryMicAccess: audio.retryMicAccess,
    ensureMicrophone: audio.ensureMicrophone,
    interruptionCount: Math.max(capturedInterruptions, audio.interruptionCount),
    interruptionEvents: audio.interruptionEvents,
    audioStream: audio.mediaStream,
    mixedAudioStream: audio.mixedAudioStream,
    isServerSttFallbackActive: audio.isServerSttFallbackActive,
    activateServerSttFallback: audio.activateServerSttFallback,

    // Timer
    secondsRemaining: timer.secondsRemaining,
    formattedTime: timer.formattedTime,
    isLowTime: timer.isLowTime,

    // State & Status
    state: currentSimulationState,
    connectionStatus:
      currentSimulationState === 'completing' || currentSimulationState === 'completed'
        ? 'evaluating'
        : currentSimulationState === 'processing'
        ? 'processing'
        : currentSimulationState === 'error'
        ? 'error'
        : 'active',

    // UI state
    activeTab,
    setActiveTab,
    manualText,
    setManualText,
    turnEndedModal,
    setTurnEndedModal,
    maxTurns:
      lifecycle.briefing?.maxTurns ||
      (lifecycle.briefing as any)?.conversationConstraints?.maxTurns ||
      (lifecycle.briefing as any)?.endingRules?.maxTurns ||
      20,

    // Actions
    submitLearnerTurn,
    handleInstantSubmit,
    handleBargeIn,
    endSimulation,
    stopAll: audio.stopAll,
    preloadAndDecodeAudio: audio.preloadAndDecodeAudio,
    speakText: audio.speakText,
    startListening: audio.startListening,
    stopListening: audio.stopListening,
    stopSpeaking: audio.stopSpeaking,
    audio,

    // Feature Flags & Configuration
    videoAnalysisEnabled: lifecycle.videoAnalysisEnabled,
    videoAnalysisConfig: lifecycle.videoAnalysisConfig,
    effectiveTTSProvider: lifecycle.effectiveTTSProvider,
    // Authoritative attempt voice contract (voice + engine + gender) resolved once
    // at startup; consumers must prefer this over any locally derived value.
    voiceContract: lifecycle.voiceContract,
  };
}
